import { addDays, differenceInCalendarDays } from "date-fns";

import type { TranslationKey } from "@/constants/i18n";
import type { EffectivePolicy, Profile } from "@/meta/types";

/** How early to start nagging. */
// ponytail: fixed window; make it a policy column if the org wants to tune it.
export const EXPIRY_WARNING_DAYS = 5;

/**
 * Days left before the password expires, or `null` when it never does.
 *
 * The backend has no such field: it only decides *at sign-in* whether the
 * password is already too old. The two halves are already on the wire —
 * `last_password_change_at` from `/me`, the interval from the effective
 * policy — so the countdown is arithmetic, not another endpoint.
 *
 * Negative means already past due; the next sign-in will refuse.
 */
export function daysUntilPasswordExpiry(
  profile: Pick<Profile, "last_password_change_at">,
  policy: Pick<EffectivePolicy, "password_change_interval_days"> | undefined,
): number | null {
  const interval = policy?.password_change_interval_days;
  if (!interval || interval <= 0 || !profile.last_password_change_at) return null;

  const expiresAt = addDays(new Date(profile.last_password_change_at), interval);
  return differenceInCalendarDays(expiresAt, new Date());
}

/**
 * Why the screen opened, or null when the person simply chose to be here.
 *
 * Four things route here — a forced change, the expiry warning, the API walling
 * the app off mid-session, and the profile menu — and only the last of those is
 * a decision the person made. The rest owe them a sentence.
 */
export function reasonKey(forced: boolean, days: number | null): TranslationKey | null {
  if (forced) return "pw.forced";
  if (days === null || days > EXPIRY_WARNING_DAYS) return null;
  if (days < 0) return "pw.expired";
  if (days === 0) return "pw.today";
  if (days === 1) return "pw.inOneDay";
  return "pw.inDays";
}
